import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase, type ShopVendor, type ShopVendorUser } from '@pc/db';
import { useAuth } from './AuthProvider';

const LS_ACTIVE_VENDOR = 'pc.activeVendorId';

/**
 * A storefront the caller can act on: the membership row + the vendor row
 * it points at. Sell pages always read state through here so they keep
 * working when the user owns more than one storefront.
 */
export interface VendorMembership {
  membership: ShopVendorUser;
  vendor: ShopVendor;
}

interface VendorCtxValue {
  /** The currently active storefront. Null when the user has none. */
  vendor: ShopVendor | null;
  /** Membership row paired with `vendor`. Null when no active storefront. */
  membership: ShopVendorUser | null;
  /** All storefronts the caller has membership in, hydrated once per user. */
  available: VendorMembership[];
  /** True once we've finished the initial hydrate. */
  ready: boolean;
  /** Switch active storefront (must already exist in `available`). */
  setActiveVendor: (vendorId: string) => void;
  /** Forget the active selection (used by "Switch storefront"). */
  clearActiveVendor: () => void;
  /** Re-fetch memberships + vendor rows from the DB. */
  refresh: () => Promise<void>;
}

const Ctx = createContext<VendorCtxValue | null>(null);

async function fetchAvailable(userId: string): Promise<VendorMembership[]> {
  // 1. memberships for this user, oldest-first so a single-storefront user
  // gets a stable default.
  const { data: mems, error: me } = await (supabase() as any)
    .schema('shop')
    .from('vendor_users')
    .select('*')
    .eq('user_id', userId)
    .is('archived_at', null)
    .order('created_at', { ascending: true });
  if (me) throw me;
  const memberships = (mems ?? []) as ShopVendorUser[];
  if (memberships.length === 0) return [];

  // 2. fetch the vendor row for each membership in one round-trip.
  const vendorIds = memberships.map((m) => m.vendor_id);
  const { data: vendors, error: ve } = await (supabase() as any)
    .schema('shop')
    .from('vendors')
    .select('*')
    .in('id', vendorIds);
  if (ve) throw ve;
  const byId = new Map<string, ShopVendor>();
  for (const v of (vendors ?? []) as ShopVendor[]) byId.set(v.id, v);

  const out: VendorMembership[] = [];
  for (const m of memberships) {
    const v = byId.get(m.vendor_id);
    if (v) out.push({ membership: m, vendor: v });
  }
  return out;
}

/**
 * Hydrates the calling auth user's vendor memberships. The sell app
 * requires at least one membership; users with zero are routed to /signup.
 * Users with one or more pick an active storefront via a picker page that
 * reads through this context.
 *
 * Mirrors apps/web/src/auth/ActiveContextProvider for cross-tab consistency:
 * the active vendor id lives in localStorage under `pc.activeVendorId`
 * and a `storage` event syncs every other tab.
 */
export function VendorContextProvider({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const [available, setAvailable] = useState<VendorMembership[]>([]);
  const [activeVendorId, setActiveVendorIdState] = useState<string | null>(
    null,
  );
  const [ready, setReady] = useState(false);

  // Once-per-user hydrate guard; same shape as ActiveContextProvider so
  // SIGNED_IN + INITIAL_SESSION + TOKEN_REFRESHED don't trigger
  // redundant refetches (which historically raced with the user's
  // setActiveVendor and bounced them back to the picker).
  const hydratedForUserId = useRef<string | null>(null);

  const userId = session?.user.id ?? null;

  const refresh = useCallback(async () => {
    if (!userId) {
      setAvailable([]);
      setActiveVendorIdState(null);
      return;
    }
    const list = await fetchAvailable(userId);
    setAvailable(list);

    const ids = list.map((m) => m.vendor.id);
    const saved = localStorage.getItem(LS_ACTIVE_VENDOR);
    const validSaved = saved && ids.includes(saved) ? saved : null;
    // Auto-select if there's only one storefront; otherwise the picker
    // page will set the active id.
    const next = validSaved ?? (ids.length === 1 ? ids[0]! : null);
    setActiveVendorIdState(next);
    if (next) {
      localStorage.setItem(LS_ACTIVE_VENDOR, next);
    } else if (saved && !validSaved) {
      // Stale id (storefront archived or memberships changed); drop it.
      localStorage.removeItem(LS_ACTIVE_VENDOR);
    }
  }, [userId]);

  useEffect(() => {
    if (loading) return;
    if (!userId) {
      hydratedForUserId.current = null;
      setAvailable([]);
      setActiveVendorIdState(null);
      setReady(true);
      return;
    }
    if (hydratedForUserId.current === userId) return;
    hydratedForUserId.current = userId;
    setReady(false);
    refresh().finally(() => setReady(true));
  }, [loading, userId, refresh]);

  const setActiveVendor = useCallback(
    (vendorId: string) => {
      // Same-vendor no-op so a redundant click in the picker doesn't
      // trigger a re-render storm.
      if (vendorId === activeVendorId) {
        localStorage.setItem(LS_ACTIVE_VENDOR, vendorId);
        return;
      }
      setActiveVendorIdState(vendorId);
      localStorage.setItem(LS_ACTIVE_VENDOR, vendorId);
    },
    [activeVendorId],
  );

  const clearActiveVendor = useCallback(() => {
    setActiveVendorIdState(null);
    localStorage.removeItem(LS_ACTIVE_VENDOR);
  }, []);

  // Cross-tab sync. When tab A switches storefront or signs out, tab B
  // needs to follow. Same belt-and-braces pattern as the operator app.
  useEffect(() => {
    function syncFromStorage() {
      const stored = localStorage.getItem(LS_ACTIVE_VENDOR);
      if (stored !== activeVendorId) setActiveVendorIdState(stored);
    }
    function onStorage(e: StorageEvent) {
      if (e.key === LS_ACTIVE_VENDOR || e.key === null) syncFromStorage();
    }
    function onPageShow(e: PageTransitionEvent) {
      if (e.persisted) syncFromStorage();
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [activeVendorId]);

  const active = useMemo(
    () => available.find((m) => m.vendor.id === activeVendorId) ?? null,
    [available, activeVendorId],
  );

  const value = useMemo<VendorCtxValue>(
    () => ({
      vendor: active?.vendor ?? null,
      membership: active?.membership ?? null,
      available,
      ready,
      setActiveVendor,
      clearActiveVendor,
      refresh,
    }),
    [active, available, ready, setActiveVendor, clearActiveVendor, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVendorContext(): VendorCtxValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useVendorContext must be used inside <VendorContextProvider>.');
  return v;
}
